import React, { useState } from 'react';
import BossDialog from '../../../components/BossDialog';

export const ATTACH_EPISODE_OPTION = {
  THIS_SLOT_ONLY: 'THIS_SLOT_ONLY',
  PRIMARY_SLOTS: 'PRIMARY_SLOTS',
  PRIMARY_AND_REPEAT: 'PRIMARY_AND_REPEAT'
};

export const REPEAT_DISTRIBUTION_MODE = {
  FREQUENCY: 'FREQUENCY',
  WEEKLY: 'WEEKLY'
};

const WEEK_DAYS = [
  { id: 1, name: "Mon" },
  { id: 2, name: "Tue" },
  { id: 3, name: "Wed" },
  { id: 4, name: "Thu" },
  { id: 5, name: "Fri" },
  { id: 6, name: "Sat" },
  { id: 0, name: "Sun" }
];

const AttachEpisodeOptionsDialog = ({
  visible,
  mediaEpisode,
  slot,
  onClose,
  onSubmit
}) => {
  const [selectedOption, setSelectedOption] = useState(ATTACH_EPISODE_OPTION.THIS_SLOT_ONLY);
  const [fromEpisode, setFromEpisode] = useState(mediaEpisode?.EpisodeNumber || 1);
  const [toEpisode, setToEpisode] = useState((mediaEpisode?.EpisodeNumber || 1) + 10);
  const [repeatEpisodes, setRepeatEpisodes] = useState(true);
  const [repeatMode, setRepeatMode] = useState(REPEAT_DISTRIBUTION_MODE.FREQUENCY);
  const [repeatFrequency, setRepeatFrequency] = useState(3);
  const [repeatDays, setRepeatDays] = useState([6, 0]); // Saturday and Sunday by default

  if (!visible) return null;

  const handleToggleDay = (dayId) => {
    setRepeatDays((prev) =>
      prev.includes(dayId) ? prev.filter((d) => d !== dayId) : [...prev, dayId]
    );
  };

  const handleConfirm = () => {
    onSubmit({
      option: selectedOption,
      fromEpisode: parseInt(fromEpisode, 10) || 1,
      toEpisode: parseInt(toEpisode, 10) || 1,
      repeatEpisodes: repeatEpisodes,
      repeatMode: repeatMode,
      repeatFrequency: Math.max(1, parseInt(repeatFrequency, 10) || 1),
      repeatDays: repeatDays
    });
  };

  return (
    <BossDialog
      title="Attach Episode to Custom Slot"
      width="520px"
      onClose={onClose}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px", padding: "8px 0" }}>
        {/* Selected Media Info */}
        <div style={{ padding: "10px 12px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
            {mediaEpisode?.Title || "Selected Episode"}
          </div>
          <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
            Episode #{mediaEpisode?.EpisodeNumber || "N/A"} &bull; {slot?.Description || slot?.Title || slot?.Slot?.Description || "Custom Slot"}
          </div>
        </div>

        {/* Radio Options */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <label style={{ fontSize: "12px", fontWeight: 600, color: "#475569", textTransform: "uppercase" }}>
            Attachment Scope
          </label>

          {[
            {
              value: ATTACH_EPISODE_OPTION.THIS_SLOT_ONLY,
              label: "Only to this slot",
              description: "Attaches only this single episode to the selected custom slot.",
              isBatch: false
            },
            {
              value: ATTACH_EPISODE_OPTION.PRIMARY_SLOTS,
              label: "To Primary slots",
              description: "Sequential episode assignment to future Primary custom slots of this slot.",
              isBatch: true
            },
            {
              value: ATTACH_EPISODE_OPTION.PRIMARY_AND_REPEAT,
              label: "To Primary with Repeat slots",
              description: "Sequential episode assignment to future Primary and Repeat custom slots of this slot.",
              isBatch: true
            }
          ].map((item) => {
            const isSelected = selectedOption === item.value;
            return (
              <div
                key={item.value}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: isSelected ? "1.5px solid #2563eb" : "1px solid #e2e8f0",
                  backgroundColor: isSelected ? "#f8fafc" : "#ffffff",
                  transition: "all 0.15s ease"
                }}
              >
                <div
                  onClick={() => setSelectedOption(item.value)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    cursor: "pointer"
                  }}
                >
                  <input
                    type="radio"
                    name="attachEpisodeOption"
                    value={item.value}
                    checked={isSelected}
                    onChange={() => setSelectedOption(item.value)}
                    style={{ marginTop: "3px", cursor: "pointer", accentColor: "#2563eb" }}
                  />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                      {item.label}
                    </span>
                    <span style={{ fontSize: "11.5px", color: "#64748b", marginTop: "2px" }}>
                      {item.description}
                    </span>
                  </div>
                </div>

                {/* Show inputs directly below this option only when selected */}
                {isSelected && item.isBatch && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                      marginTop: "6px",
                      padding: "12px",
                      backgroundColor: "#ffffff",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1"
                    }}
                  >
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
                          From Episode #
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="form-control form-control-sm"
                          value={fromEpisode}
                          onChange={(e) => setFromEpisode(e.target.value)}
                        />
                      </div>

                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
                          To Episode #
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="form-control form-control-sm"
                          value={toEpisode}
                          onChange={(e) => setToEpisode(e.target.value)}
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                      <input
                        type="checkbox"
                        id={`repeatEpisodesCheck_${item.value}`}
                        checked={repeatEpisodes}
                        onChange={(e) => setRepeatEpisodes(e.target.checked)}
                        style={{ cursor: "pointer", width: "16px", height: "16px", accentColor: "#2563eb" }}
                      />
                      <label
                        htmlFor={`repeatEpisodesCheck_${item.value}`}
                        style={{ fontSize: "12px", color: "#334155", cursor: "pointer", marginBottom: 0 }}
                      >
                        Repeat from start episode when last episode is reached
                      </label>
                    </div>

                    {/* Additional settings for Primary with Repeat slots */}
                    {item.value === ATTACH_EPISODE_OPTION.PRIMARY_AND_REPEAT && (
                      <div
                        style={{
                          marginTop: "8px",
                          paddingTop: "10px",
                          borderTop: "1px dashed #cbd5e1",
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontSize: "12px", fontWeight: 600, color: "#334155" }}>
                            Repeat Slot Assignment Mode:
                          </span>
                          <div style={{ display: "flex", gap: "12px" }}>
                            <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", marginBottom: 0 }}>
                              <input
                                type="radio"
                                name="repeatMode"
                                value={REPEAT_DISTRIBUTION_MODE.FREQUENCY}
                                checked={repeatMode === REPEAT_DISTRIBUTION_MODE.FREQUENCY}
                                onChange={() => setRepeatMode(REPEAT_DISTRIBUTION_MODE.FREQUENCY)}
                                style={{ accentColor: "#2563eb", cursor: "pointer" }}
                              />
                              By Frequency
                            </label>
                            <label style={{ fontSize: "12px", display: "flex", alignItems: "center", gap: "5px", cursor: "pointer", marginBottom: 0 }}>
                              <input
                                type="radio"
                                name="repeatMode"
                                value={REPEAT_DISTRIBUTION_MODE.WEEKLY}
                                checked={repeatMode === REPEAT_DISTRIBUTION_MODE.WEEKLY}
                                onChange={() => setRepeatMode(REPEAT_DISTRIBUTION_MODE.WEEKLY)}
                                style={{ accentColor: "#2563eb", cursor: "pointer" }}
                              />
                              Weekly Days
                            </label>
                          </div>
                        </div>

                        {repeatMode === REPEAT_DISTRIBUTION_MODE.FREQUENCY ? (
                          <div className="form-group" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                            <label style={{ fontSize: "12px", fontWeight: 500, color: "#475569", marginBottom: 0, minWidth: "130px" }}>
                              Repeat Frequency:
                            </label>
                            <input
                              type="number"
                              min="1"
                              max="20"
                              className="form-control form-control-sm"
                              style={{ width: "90px" }}
                              value={repeatFrequency}
                              onChange={(e) => setRepeatFrequency(e.target.value)}
                            />
                            <span style={{ fontSize: "11px", color: "#64748b" }}>
                              times per primary episode
                            </span>
                          </div>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            <span style={{ fontSize: "11px", color: "#64748b" }}>
                              Repeat slot on selected days (e.g., Sat & Sun):
                            </span>
                            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                              {WEEK_DAYS.map((day) => {
                                const isChecked = repeatDays.includes(day.id);
                                return (
                                  <button
                                    key={day.id}
                                    type="button"
                                    onClick={() => handleToggleDay(day.id)}
                                    style={{
                                      padding: "3px 10px",
                                      fontSize: "11px",
                                      fontWeight: isChecked ? 600 : 500,
                                      borderRadius: "14px",
                                      border: isChecked ? "1px solid #2563eb" : "1px solid #cbd5e1",
                                      backgroundColor: isChecked ? "#2563eb" : "#ffffff",
                                      color: isChecked ? "#ffffff" : "#475569",
                                      cursor: "pointer",
                                      lineHeight: "1.2",
                                      transition: "all 0.15s ease"
                                    }}
                                  >
                                    {day.name}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "8px" }}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            style={{ minWidth: "80px", color: "#334155", borderColor: "#cbd5e1" }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-dark btn-sm"
            onClick={handleConfirm}
            style={{ minWidth: "80px", backgroundColor: "#1e293b", borderColor: "#1e293b", fontWeight: 600 }}
          >
            Attach
          </button>
        </div>
      </div>
    </BossDialog>
  );
};

export default AttachEpisodeOptionsDialog;
